import { useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { Button } from '@/ui/primitives';
import { NotifyDone } from '@/components/NotifyDone';
import { NotifyForm } from '@/components/NotifyForm';
import type { BandId, NotifyIntent } from '@/domain/bands';
import type { Dictionary } from '@/i18n';
import type { LeadResult } from '@/services/leads';

type NotifyInlineProps = {
  dict: Dictionary;
  intent: NotifyIntent;
  band: BandId;
  inputType: 'cep' | 'geolocation';
  onSubmit: (firstName: string, phone: string) => Promise<LeadResult>;
};

/* The same invitation the standalone page opens in a `<dialog>`, opened in place instead.
   `showModal` puts the dialog in the document's top layer, and inside a cross-origin
   iframe that document is the iframe: the backdrop darkens only the embedded rectangle,
   the dialog centres on that rectangle rather than on the screen, and a rectangle that is
   half off-screen shows half a dialog the lead cannot scroll to. On a phone the software
   keyboard scrolls the parent page, which moves the rest of the form out of view.

   Opening in place has none of that, and costs the lead nothing: the panel is already the
   only thing on this side of the section. */
export const NotifyInline = ({ dict, intent, band, inputType, onSubmit }: NotifyInlineProps) => {
  const [open, setOpen] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const copy = dict.notify[intent];

  if (recorded) {
    return <NotifyDone dict={dict} intent={intent} />;
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="accent"
        className="h-11 w-full text-[15px]"
        onClick={() => {
          setOpen(true);
        }}
      >
        <BellRing className="h-4 w-4" aria-hidden="true" />
        {copy.trigger}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-grey-200 bg-grey-100 p-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-extrabold tracking-tight text-black">{copy.title}</h3>
        <button
          type="button"
          aria-label={dict.notify.close}
          onClick={() => {
            setOpen(false);
          }}
          className="-mr-1 -mt-1 rounded p-1 text-grey-800"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <NotifyForm
        dict={dict}
        intent={intent}
        band={band}
        inputType={inputType}
        onSubmit={onSubmit}
        onRecorded={() => {
          setRecorded(true);
        }}
      />
    </div>
  );
};
